/**
 * Shared axios instance: JWT injection + 401 refresh + trace ID header.
 */
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth';
import { i18n } from '@/i18n';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api';

export const http = axios.create({
  baseURL,
  timeout: 30_000,
  withCredentials: true,   // send/receive the HttpOnly refresh cookie (same-origin)
});

/**
 * Backend signals customer wallet gas balance insufficient for sweep; backend
 * has scheduled a top-up internally. Caller should wait retryAfterSeconds и retry initiate.
 * (PWA cannot trigger gas drops directly — security invariant §1.10h.)
 */
export class GasFundingInProgressError extends Error {
  constructor(public retryAfterSeconds: number) {
    super(`GAS_FUNDING_IN_PROGRESS, retry in ${retryAfterSeconds}s`);
  }
}

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const auth = useAuthStore();
  if (auth.accessToken) {
    config.headers.set('Authorization', `Bearer ${auth.accessToken}`);
  }
  config.headers.set('X-Trace-Id', crypto.randomUUID());
  // FT §1.10g + GCARD §5.3: Accept-Language drives backend MessageSource.
  // Authenticated requests cascade-override to customer.preferred_language server-side.
  config.headers.set('Accept-Language', i18n.global.locale.value);
  return config;
});

let refreshInFlight: Promise<string> | null = null;
let endingSession = false;

/** Auth-эндпоинты (login/refresh/magic): их 401/403 — неверные креды/протухший refresh, а НЕ
 *  «сессия умерла в середине работы». Не трогаем — ошибка должна дойти до формы логина, и это
 *  предотвращает рекурсию refresh-запроса сам на себя. */
function isAuthEndpoint(url?: string): boolean {
  // /auth/ = pre-JWT login/refresh; /device-pin/ = pre-auth PIN login/available. Their 401 means
  // "wrong PIN / bad creds", NOT "session died" — must reach the caller, not trigger endSession.
  return /\/auth\/|\/device-pin\//.test(url ?? '');
}

/** Step-up + passkey-enrollment endpoints. A 401/403 here means "that factor / ceremony failed"
 *  (WebAuthn assertion rejected, challenge expired), NOT "the session died" — it must reach the
 *  caller's catch, never a logout. `register/` is included: a failed "Enable Face ID" (e.g. an expired
 *  registration challenge) returns 401 and MUST NOT sign the customer out. */
function isStepUpEndpoint(url?: string): boolean {
  return /\/webauthn\/stepup\/|\/webauthn\/register\/|\/me\/stepup\//.test(url ?? '');
}

/** Сессия мертва (истёк/отозван токен, refresh невозможен): чистим локальный auth и уводим на
 *  login. «Токен сдох» = тихий выход, а НЕ сырые 401/403 в UI (по требованию UX). */
async function endSession(): Promise<void> {
  if (endingSession) return;
  endingSession = true;
  const auth = useAuthStore();
  auth.accessToken = null;
  auth.refreshToken = null;
  auth.customerId = null;
  auth.sessionId = null;
  try {
    const { router } = await import('@/router');
    if (router.currentRoute.value.name !== 'login') {
      await router.push({ name: 'login' }).catch(() => { /* redundant nav — ignore */ });
    }
  } finally {
    endingSession = false;
  }
}

/** Никогда не резолвится — «глотает» 401/403 на время редиректа на login, чтобы вью не
 *  отрендерил сырую ошибку. Компонент размонтируется при навигации. */
function silent(): Promise<never> {
  return new Promise<never>(() => { /* intentionally never settles — redirecting to login */ });
}

http.interceptors.response.use(
  r => r,
  async (error: AxiosError) => {
    const auth = useAuthStore();
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = error.response?.status;

    // 401/403 на аутентифицированном запросе = протухший/невалидный токен (vitrine-JWT при
    // истечении отдаёт 403, не 401 — ловим оба). Один раз пробуем refresh; если refresh
    // невозможен или упал — «токен сдох»: тихий выход в login, без 403/401 в UI.
    if ((status === 401 || status === 403) && original && !isAuthEndpoint(original.url)) {
      // A step-up factor failing (passkey / app-PIN / OTP) is a domain error, not session death —
      // surface it to the chooser's catch, never log the customer out.
      if (isStepUpEndpoint(original.url)) {
        throw error;
      }
      // STEP_UP_REQUIRED (Прил. B) — это НЕ смерть сессии, а требование биометрии перед показом
      // карты. Отдаём ошибку вызывающему (onReveal сделает step-up и повторит), не логаутим.
      const errCode = (error.response?.data as { error?: string } | undefined)?.error;
      if (status === 403 && errCode === 'STEP_UP_REQUIRED') {
        throw error;
      }
      // Refresh token is in the HttpOnly cookie now — gate on the session hint (customerId), not an
      // in-store refresh token. The cookie is sent automatically via withCredentials.
      if (!original._retried && auth.customerId) {
        original._retried = true;
        try {
          refreshInFlight ??= auth.refreshAccessToken();
          const newToken = await refreshInFlight;
          refreshInFlight = null;
          original.headers.set('Authorization', `Bearer ${newToken}`);
          return http(original);
        } catch {
          refreshInFlight = null;
          await endSession();
          return silent();
        }
      }
      // Повтор после УСПЕШНОГО обновления токена всё равно отдал 403 — значит токен свежий, а
      // сервер отказал по существу: «эта операция тебе не разрешена». Это доменная ошибка, и она
      // обязана дойти до вызывающего. Раньше здесь стоял выход, и любой отказ по правам выкидывал
      // клиента из кабинета целиком: переход в участника фэмили-офиса без права VIEW_TX
      // разлогинивал человека вместо того, чтобы сказать «нет доступа».
      // 401 после обновления — другое дело: токен по-прежнему не принимают, сессия мертва.
      if (original._retried && status === 403) {
        throw error;
      }
      // Нет refresh-токена, либо повторный отказ после retry — сессия действительно мертва.
      await endSession();
      return silent();
    }

    // 503 + body { error: 'GAS_FUNDING_IN_PROGRESS' } — surface as typed error
    // for SendView к handle с countdown + auto-retry.
    if (status === 503) {
      const data = error.response?.data as { error?: string; retry_after_seconds?: number } | undefined;
      if (data?.error === 'GAS_FUNDING_IN_PROGRESS') {
        throw new GasFundingInProgressError(data.retry_after_seconds ?? 30);
      }
    }

    throw error;
  },
);
